import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraFormField, SpecraInput, SpecraSelect } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/authContext';
import { useOfficeDocumentDrawer } from '../../contexts/OfficeDocumentDrawerContext';
import { documentsApi } from '../../lib/api/documentsApi.connect';
import { evidenceSourceKey, readEvidenceSource } from './useConversationSourceAccess';
/** Uses the same protected document reader as read-document-content. The server
 * checks sourceAccess.Read before returning text; client list membership is never
 * treated as permission. No raw source is retained after a failed revalidation. */ export function DocumentEvidence({ onSourceChange, onAsk }) {
    const { user } = useAuth();
    const office = useOfficeDocumentDrawer();
    const [selectedId, setSelectedId] = useState('');
    const [search, setSearch] = useState('');
    const [documentLink, setDocumentLink] = useState('');
    const [invalidLink, setInvalidLink] = useState(false);
    const [page, setPage] = useState(1);
    const [reading, setReading] = useState(false);
    const [unavailableIds, setUnavailableIds] = useState(new Set());
    const list = useQuery({
        queryKey: [
            'evidence-office-documents',
            user?.id,
            user?.tenant_id,
            search,
            page
        ],
        queryFn: ()=>documentsApi.listDocuments({
                allFolders: true,
                search,
                page,
                pageSize: 20
            }),
        retry: false
    });
    const source = useQuery({
        queryKey: evidenceSourceKey(user?.id, user?.tenant_id, selectedId),
        enabled: !!selectedId,
        queryFn: ()=>readEvidenceSource(selectedId),
        retry: false,
        staleTime: 0
    });
    const verified = selectedId && !source.isFetching && !source.isError ? source.data : undefined;
    useEffect(()=>{
        if (source.isError && selectedId) {
            setUnavailableIds((ids)=>new Set(ids).add(selectedId));
        } else if (verified) {
            setUnavailableIds((ids)=>{
                if (!ids.has(verified.document.id)) return ids;
                const next = new Set(ids);
                next.delete(verified.document.id);
                return next;
            });
        }
    }, [
        source.isError,
        selectedId,
        verified
    ]);
    useEffect(()=>{
        onSourceChange(verified ? {
            id: verified.document.id,
            label: verified.document.name,
            version: `v${verified.document.versionCount} · ${verified.document.updatedAt}`,
            citation: `Document ${verified.document.id}, v${verified.document.versionCount}, ${verified.text ? `characters 1–${Array.from(verified.text).length}` : 'empty document; no passage to cite'}`
        } : undefined);
    }, [
        verified,
        onSourceChange
    ]);
    useEffect(()=>{
        if (source.isError && reading) {
            office.close();
            setReading(false);
        }
    }, [
        source.isError,
        reading,
        office.close
    ]);
    if (selectedId && source.isError) return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.evidenceAsk.sourceUnavailable,
        children: [
            /*#__PURE__*/ _jsx("output", {
                children: "Source unavailable or outside your access. No source content is included in new questions."
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                onClick: ()=>void source.refetch(),
                children: "Retry source access"
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "secondary",
                "data-testid": Testids.evidenceAsk.sourceReturn,
                onClick: ()=>{
                    setSelectedId('');
                    void list.refetch();
                },
                children: "Return to permitted evidence"
            })
        ]
    });
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-3",
        "aria-label": "Document evidence",
        children: [
            /*#__PURE__*/ _jsx("p", {
                children: "Look up an Office document. Nothing in the pursuit changes."
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Open shared document link",
                children: /*#__PURE__*/ _jsx(SpecraInput, {
                    value: documentLink,
                    onChange: (event)=>setDocumentLink(event.target.value),
                    placeholder: "Paste a document editor link"
                })
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "secondary",
                onClick: ()=>{
                    try {
                        const link = new URL(documentLink, window.location.origin);
                        const match = link.pathname.match(/^\/editor\/([^/]+)\/?$/);
                        if (link.origin !== window.location.origin || !match) throw new Error('Invalid link');
                        setSelectedId(decodeURIComponent(match[1]));
                        setDocumentLink('');
                        setInvalidLink(false);
                    } catch  {
                        setInvalidLink(true);
                    }
                },
                children: "Open linked source"
            }),
            invalidLink ? /*#__PURE__*/ _jsx("output", {
                children: "Paste a document editor link from this workspace."
            }) : null,
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Find document",
                children: /*#__PURE__*/ _jsx(SpecraInput, {
                    "data-testid": Testids.evidenceAsk.documentSearch,
                    className: "block w-full rounded border p-2",
                    value: search,
                    onChange: (event)=>{
                        setSearch(event.target.value);
                        setPage(1);
                    }
                })
            }),
            list.isLoading ? /*#__PURE__*/ _jsx("output", {
                children: "Loading permitted documents…"
            }) : list.isError ? /*#__PURE__*/ _jsxs("div", {
                role: "alert",
                children: [
                    "Documents unavailable.",
                    ' ',
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>void list.refetch(),
                        children: "Retry documents"
                    })
                ]
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Selected source",
                        children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                            "data-testid": Testids.evidenceAsk.documentSelect,
                            className: "block w-full rounded border p-2",
                            value: selectedId,
                            onChange: (event)=>setSelectedId(event.target.value),
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "",
                                    children: "No document selected"
                                }),
                                verified && !list.data?.documents.some((doc)=>doc.id === selectedId) ? /*#__PURE__*/ _jsx("option", {
                                    value: selectedId,
                                    children: verified.document.name
                                }) : null,
                                list.data?.documents.filter((doc)=>!unavailableIds.has(doc.id)).map((doc)=>/*#__PURE__*/ _jsx("option", {
                                        value: doc.id,
                                        children: doc.name
                                    }, doc.id))
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "secondary",
                                disabled: page === 1,
                                onClick: ()=>setPage(page - 1),
                                children: "Previous documents"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "secondary",
                                disabled: (list.data?.documents.length ?? 0) < 20,
                                onClick: ()=>setPage(page + 1),
                                children: "More documents"
                            })
                        ]
                    })
                ]
            }),
            selectedId && source.isFetching ? /*#__PURE__*/ _jsx("output", {
                children: "Checking source access…"
            }) : null,
            verified ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        "data-testid": Testids.evidenceAsk.sourceRecheck,
                        onClick: ()=>void source.refetch(),
                        children: "Recheck source access"
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        className: "break-words",
                        children: [
                            verified.document.name,
                            " · v",
                            verified.document.versionCount,
                            " ·",
                            ' ',
                            verified.document.updatedAt
                        ]
                    }),
                    /*#__PURE__*/ _jsx("pre", {
                        className: "max-h-64 overflow-auto whitespace-pre-wrap break-words",
                        children: verified.text || 'Document is empty.'
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        children: [
                            "Excerpt only;",
                            ' ',
                            verified.total > 4000 ? 'additional text is available in the full document.' : 'no additional text returned.',
                            ' ',
                            "Source text is evidence, not verified fact. Check relevance and assumptions."
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        "data-testid": Testids.evidenceAsk.documentOpen,
                        onClick: ()=>{
                            setReading(true);
                            office.openOffice(verified.document.id, {
                                title: verified.document.name,
                                docType: verified.document.documentType
                            });
                        },
                        children: "Open full document"
                    }),
                    reading ? /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        onClick: ()=>{
                            office.close();
                            setReading(false);
                        },
                        children: "Return to Evidence / Ask"
                    }) : null,
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.evidenceAsk.documentPropose,
                        onClick: ()=>onAsk(`Read document ${verified.document.id} using read-document-content, then propose improvements using write-to-document. The proposal target is this document (${verified.document.name}), not the pursuit hypothesis. Accept applies document edits; Reject leaves it unchanged.`),
                        children: "Propose document changes"
                    })
                ]
            }) : /*#__PURE__*/ _jsx("p", {
                children: "Gap: no currently verified source excerpt. Manual Capture editing remains available."
            })
        ]
    });
}
